import * as signalR from '@microsoft/signalr'
import { ref, readonly } from 'vue'
import { getAccessToken } from '@/plugins/auth/msalConfig'
import { useSnackbar } from './useSnackbar'

export enum NotificationEventType {
  Success = 0,
  Error = 1,
  Info = 2,
  Warning = 3,
}

export interface NotificationPayload {
  eventType: NotificationEventType
  message: string
  entityType: string
  entityId?: number
}

const connection = ref<signalR.HubConnection | null>(null)
const isConnected = ref(false)

export function useSignalR() {
  const { success, error, warning, info } = useSnackbar()

  const connect = async (): Promise<void> => {
    if (connection.value?.state === signalR.HubConnectionState.Connected) {
      return
    }

    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
    const hubUrl = baseUrl.replace('/api/v1', '') + '/hubs/notifications'

    connection.value = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const token = await getAccessToken()
          return token || ''
        },
      })
      .withAutomaticReconnect()
      .build()

    connection.value.onreconnecting(() => {
      isConnected.value = false
    })

    connection.value.onreconnected(() => {
      isConnected.value = true
    })

    connection.value.onclose(() => {
      isConnected.value = false
    })

    connection.value.on('ReceiveNotification', (payload: NotificationPayload) => {
      switch (payload.eventType) {
        case NotificationEventType.Success:
          success(payload.message)
          break
        case NotificationEventType.Error:
          error(payload.message)
          break
        case NotificationEventType.Warning:
          warning(payload.message)
          break
        case NotificationEventType.Info:
          info(payload.message)
          break
      }
    })

    try {
      await connection.value.start()
      isConnected.value = true
    }
    catch (err) {
      console.error('SignalR connection error:', err)
    }
  }

  return {
    isConnected: readonly(isConnected),
    connect,
  }
}
